import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useChildRunsQueryParams } from '@/Pages/Run/utils/useChildRunsQueryParams';
import { useGroupChildByParentId } from '@/Pages/Run/utils/useGroupChildByParentId';
import { useRootRunAndRun } from '@/Pages/Run/utils/useRootRunAndRun';
import { useRunsInTreeStructure } from '@/Pages/Run/utils/useRunsInTreeStructure';

import { useRun, useRunsQuery } from './useSwr';

const useGetFlattenedRuns = ({
  traceId,
  runId,
  sessionId,
}: {
  traceId?: string;
  runId?: string;
  sessionId?: string;
}) => {
  const { datasetShareToken } = useParams();

  // we only need to get this for the full trace

  const { rootRun, run } = useRootRunAndRun(
    traceId,
    traceId,
    undefined,
    undefined,
    undefined,
    sessionId
  );

  const runsQuery = useChildRunsQueryParams({
    traceId: rootRun?.trace_id,
    run: run?.data,
    rootRun,
  });

  const childRuns = useRunsQuery(
    runsQuery,
    { datasetShareToken },
    {
      keepPreviousData: false,
    }
  );

  const _childRunLookup = useGroupChildByParentId(
    childRuns.data?.runs,
    childRuns.isLoading
  );
  const childRunLookup = rootRun ? _childRunLookup : undefined;

  const { tree } = useRunsInTreeStructure({
    rootRuns: rootRun ? [rootRun] : [],
    run: run?.data,
    childRunLookup: childRunLookup ?? {},
    filteredRuns: childRuns.data?.runs,
    filteredOnly: false,
    userAllRuns: true,
    foldedRuns: [],
  });

  const { flattenedRuns } = tree;
  return {
    flattenedRuns:
      !!childRuns?.data && !!run?.data && runId ? flattenedRuns : [],
    childRuns,
  };
};

export const useGetTrackedComparisonRun = ({
  rootBaseRunId,
  rootCompareRunId,
  isAutoTracking,
  setComparisonRunId,
  setBaseRunId,
  baseRunSessionId,
}: {
  rootBaseRunId?: string;
  rootCompareRunId?: string;
  isAutoTracking: boolean;
  setComparisonRunId: (runId: string | null) => void;
  setBaseRunId: (runId: string | null) => void;
  baseRunSessionId?: string;
}) => {
  const { datasetShareToken } = useParams();

  const disableGetRunRequests = !rootCompareRunId || !rootBaseRunId;
  const rootComparisonRun = useRun({
    id: disableGetRunRequests ? undefined : rootCompareRunId,
    options: { keepPreviousData: false },
    excludeSerialized: true,
    sessionId: baseRunSessionId,
  });
  const rootBaseRun = useRun({
    id: disableGetRunRequests ? undefined : rootBaseRunId,
    options: { keepPreviousData: false },
    excludeSerialized: true,
    sessionId: baseRunSessionId,
  });

  const { flattenedRuns: peekedFlattenedRuns } = useGetFlattenedRuns({
    traceId: rootBaseRun?.data?.trace_id,
    runId: rootBaseRun?.data?.id,
    sessionId: baseRunSessionId,
  });

  // if no comparison run is defined we can use same sessionId and startTime as base run
  const { flattenedRuns: compareFlattenedRuns } = useGetFlattenedRuns({
    traceId: rootComparisonRun?.data?.trace_id,
    runId: rootComparisonRun?.data?.id,
    sessionId: baseRunSessionId,
  });

  const [autoTrackingIndex, setAutoTrackingIndex] = useState<
    number | undefined
  >(undefined);

  useEffect(() => {
    // set the auto tracking index when the peeked flattened runs are loaded
    // and re-set the index if we no longer have a comparison run
    if (
      peekedFlattenedRuns.length > 0 &&
      (!autoTrackingIndex || !rootCompareRunId)
    ) {
      setAutoTrackingIndex(
        peekedFlattenedRuns.findIndex((r) => r.run.id === rootBaseRunId)
      );
    }
  }, [peekedFlattenedRuns, rootCompareRunId]);

  // get the two matching runs to compare against each other
  const { comparisonRunId, baseRunId } = useMemo(() => {
    // if we're not auto tracking, then we just return the compare run
    if (!isAutoTracking || !rootCompareRunId || !autoTrackingIndex) {
      return {
        comparisonRunId: rootCompareRunId,
        baseRunId: rootBaseRunId,
      };
    }
    const baseIndex = Math.min(
      autoTrackingIndex,
      peekedFlattenedRuns.length ? peekedFlattenedRuns.length - 1 : 0
    );
    const comparisonIndex = Math.min(
      autoTrackingIndex,
      compareFlattenedRuns.length ? compareFlattenedRuns.length - 1 : 0
    );

    return {
      comparisonRunId: compareFlattenedRuns[comparisonIndex]?.run.id,
      baseRunId: peekedFlattenedRuns[baseIndex]?.run.id,
    };
  }, [
    isAutoTracking,
    rootCompareRunId,
    rootBaseRunId,
    compareFlattenedRuns,
    peekedFlattenedRuns,
    autoTrackingIndex,
  ]);

  const comparisonRun = useRun({
    id: comparisonRunId,
    options: { keepPreviousData: false },
    withContents: true,
    excludeSerialized: true,
    sessionId: baseRunSessionId,
  });
  const baseRun = useRun({
    id: baseRunId,
    shareToken: { datasetShareToken },
    options: { keepPreviousData: true },
    withContents: true,
    excludeSerialized: true,
    sessionId: baseRunSessionId,
  });

  const handleUpdateAutoTrackingIndex = (
    runId: string,
    type: 'peeked' | 'compare'
  ) => {
    let index;
    if (type === 'peeked') {
      index = peekedFlattenedRuns.findIndex((r) => r.run.id === runId);
      const comparisonIndex = Math.min(index, compareFlattenedRuns.length - 1);
      if (comparisonIndex !== -1) {
        setComparisonRunId(compareFlattenedRuns[comparisonIndex]?.run.id);
      }
    } else {
      index = compareFlattenedRuns.findIndex((r) => r.run.id === runId);
      const baseIndex = Math.min(index, peekedFlattenedRuns.length - 1);
      if (baseIndex !== -1) {
        setBaseRunId(peekedFlattenedRuns[baseIndex]?.run.id);
      }
    }
    setAutoTrackingIndex(index);
  };

  return {
    comparisonRun,
    baseRun,
    handleUpdateAutoTrackingIndex,
  };
};
